import { FC, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from '@frontend/i18n';
import { Modal } from '@frontend/design-system';
import { useConfigShallowStore } from '../../../../stores';
import { useIdleTimeout } from '../../hooks/use-idle-timeout';
import { modalActionsStyles, modalBodyStyles } from './kiosk-idle-redirect.styles';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_MS = 60 * 1000;

interface KioskIdleRedirectProps {
  locationId: string;
}

const noop = () => undefined;

/** Sends an idle kiosk patron back to the kiosk home, warning them for the last minute. */
export const KioskIdleRedirect: FC<KioskIdleRedirectProps> = ({ locationId }) => {
  const { t } = useTranslation('digital-forms-experience');
  const navigate = useNavigate();
  const { setPerson } = useConfigShallowStore('setPerson');

  const redirectToKiosk = useCallback(() => {
    // ignoreBlocker: the unsaved-changes prompt must not hold an abandoned kiosk session open
    // Clear the patron only after navigation commits, otherwise the form's missing-patron redirect effect re-navigates mid-transition
    navigate({ to: `/kiosk/${locationId}`, replace: true, ignoreBlocker: true }).finally(() => setPerson(undefined));
  }, [navigate, locationId, setPerson]);

  const { secondsRemaining, extend } = useIdleTimeout({
    timeoutMs: IDLE_TIMEOUT_MS,
    warningMs: WARNING_MS,
    onTimeout: redirectToKiosk,
  });

  const isWarningVisible = secondsRemaining !== null;
  const originalTitleRef = useRef('');

  useEffect(() => {
    if (!isWarningVisible) return;

    originalTitleRef.current = document.title;
    return () => {
      document.title = originalTitleRef.current;
    };
  }, [isWarningVisible]);

  useEffect(() => {
    if (secondsRemaining !== null) {
      document.title = t('Redirecting in {{count}} seconds', { count: secondsRemaining });
    }
  }, [secondsRemaining, t]);

  return (
    <Modal show={isWarningVisible} onClose={noop} maxWidth={500} disableCloseOnEscape disableCloseOnOverlayClick>
      <Modal.Header>{t('Redirecting to Kiosk in {{count}} seconds', { count: secondsRemaining ?? 0 })}</Modal.Header>
      <Modal.Body css={modalBodyStyles}>{t('Do you want to extend this session or return to Kiosk?')}</Modal.Body>
      <Modal.Actions
        css={modalActionsStyles}
        primaryLabel={t('Yes: extend my session')}
        secondaryLabel={t('No: return to Kiosk')}
        onPrimaryClick={extend}
        onSecondaryClick={redirectToKiosk}
      />
    </Modal>
  );
};
